import React from 'react';
import { MatchupTeamEntry } from '@/api/schemas';
import { getAbbreviation } from '@/utils/teams';
import { OPRK_TOOLTIP } from '@/utils/playerDisplay';

// ============================================================================
// Defensive ranks table — two rows (home + away) for one match.
// Visual parity with Top10Table: yellow-bordered card, dark navy bg, sticky left
// team column, yellow-accent OPRK column (plays the visual role of PTS).
// ============================================================================

interface MatchupDefenseTableProps {
  home: MatchupTeamEntry;
  away: MatchupTeamEntry;
}

// Column keys match the backend response (and projection_service stat keys).
// `label` is what renders in the header — short so mobile columns stay tight.
const STAT_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'Goal', label: 'GL' },
  { key: 'ShotOnTarget', label: 'SoT' },
  { key: 'Shot', label: 'Sh' },
  { key: 'GCA', label: 'GCA' },
  { key: 'SCA', label: 'SCA' },
  { key: 'ProgPass', label: 'IP' },
  { key: 'ProgCarry', label: 'IC' },
  { key: 'Dispossessed', label: 'DIS' },
];

// English ordinal suffix: 1→1st, 2→2nd, 3→3rd, 4→4th, 11→11th, 21→21st, …
const ordinalize = (n: number): string => {
  const abs = Math.abs(n);
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (abs % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatRank = (v: number | null | undefined): string =>
  v == null ? '—' : ordinalize(v);

const RankCell: React.FC<{ value: number | null | undefined; highlight?: boolean }> = ({
  value,
  highlight,
}) => (
  <td
    className={[
      'px-2 py-3 sm:px-3 sm:py-4 text-center whitespace-nowrap',
      highlight
        ? 'font-heading font-bold text-lg sm:text-xl tracking-wider text-accent-yellow bg-accent-yellow/5'
        : 'font-mono font-medium text-sm text-gray-200',
      value == null ? (highlight ? '' : 'text-gray-600') : '',
    ].join(' ')}
  >
    {formatRank(value)}
  </td>
);

const TeamRow: React.FC<{ team: MatchupTeamEntry; isLast: boolean; isEven: boolean }> = ({
  team,
  isLast,
  isEven,
}) => {
  const abbr = getAbbreviation(team.team_name) || team.team_name;
  return (
    <tr
      className={[
        isEven ? 'bg-primary-dark/30' : '',
        isLast ? '' : 'border-b border-gray-700/40',
      ].join(' ')}
    >
      {/* Team (sticky) */}
      <td className="py-3 sm:py-4 pl-2 sm:pl-6 pr-2 text-left whitespace-nowrap sticky left-0 bg-surface-card z-[5]">
        <span className="font-body font-bold text-[13px] sm:text-base text-white tracking-tight">
          {abbr}
        </span>
        <span className="ml-1.5 sm:ml-2 inline-block align-middle px-1.5 sm:px-2 py-0 sm:py-0.5 rounded bg-gray-200/10 text-gray-300 font-mono font-bold text-micro sm:text-badge tracking-wider whitespace-nowrap">
          {team.team_name}
        </span>
      </td>
      {/* OPRK (highlighted) */}
      <RankCell value={team.ranks?.oprk} highlight />
      {/* Per-stat ranks */}
      {STAT_COLUMNS.map(({ key }) => (
        <RankCell key={key} value={team.ranks?.[key]} />
      ))}
    </tr>
  );
};

const MatchupDefenseTable: React.FC<MatchupDefenseTableProps> = ({ home, away }) => {
  return (
    <div className="w-full mb-4">
      <div className="relative overflow-hidden rounded-lg border border-accent-yellow/60 bg-surface-card shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* ===== Header ===== */}
            <thead>
              <tr className="bg-accent-yellow/10 border-b border-accent-yellow/60">
                <th
                  className="text-left pl-2 sm:pl-6 py-3 sm:py-4 font-heading font-bold text-sm sm:text-lg tracking-[1.5px] sm:tracking-[2px] uppercase text-white whitespace-nowrap sticky left-0 bg-surface-raised z-10"
                >
                  DEFENSIVE RANKS
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-accent-yellow/10 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap" title={OPRK_TOOLTIP}>
                  OPRK
                </th>
                {STAT_COLUMNS.map(({ label }) => (
                  <th
                    key={label}
                    className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            {/* ===== Body ===== */}
            <tbody>
              <TeamRow team={home} isEven={false} isLast={false} />
              <TeamRow team={away} isEven={true} isLast={true} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MatchupDefenseTable;
